import type { HistoryItem } from '@tive/core';
import { Bot, RotateCcw, RotateCw, TriangleAlert, User } from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';

/**
 * Everything that has happened to this scenario, newest first.
 *
 * Distinct from the change list next door, and the difference is the point: the
 * change list is what the plan *is*, deduplicated and revertable; this is what
 * people *did* to it, in order, including the things they undid. Somebody
 * moved twice and back shows as one entry there and three here.
 */
export function HistoryList() {
  const history = useAppStore((s) => s.history);
  const scenarioId = useAppStore((s) => s.scenario?.id);
  const revision = useAppStore((s) => s.scenario?.revision);
  const loadHistory = useAppStore((s) => s.loadHistory);

  // Re-read whenever the scenario changes underneath — including from Claude,
  // which bumps the revision without this tab having done anything.
  useEffect(() => {
    void loadHistory();
  }, [loadHistory, scenarioId, revision]);

  if (!history) {
    return <p className="px-3 py-6 text-center text-[12px] text-[var(--color-ink-3)]">Loading history…</p>;
  }

  if (history.items.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-[12.5px] text-[var(--color-ink-2)]">Nothing has happened yet</p>
        <p className="mt-1 text-[11.5px] text-[var(--color-ink-3)]">
          Every change to this plan will be listed here, with who made it.
        </p>
      </div>
    );
  }

  return (
    <ol className="h-full overflow-y-auto py-1">
      {history.items.map((item) => (
        <Row key={item.seq} item={item} />
      ))}
    </ol>
  );
}

function Row({ item }: { item: HistoryItem }) {
  const marker = item.kind !== 'op';

  return (
    <li
      className={cn(
        'flex gap-2.5 px-3 py-2 text-[12.5px]',
        // An undone change is still a fact about what happened, so it stays
        // legible rather than being hidden or greyed into nothing.
        item.undone && 'opacity-55',
      )}
    >
      <span className="mt-0.5 shrink-0 text-[var(--color-ink-3)]">
        {item.kind === 'undo' ? (
          <RotateCcw size={13} />
        ) : item.kind === 'redo' ? (
          <RotateCw size={13} />
        ) : item.actor?.id === 'claude' ? (
          <Bot size={13} />
        ) : (
          <User size={13} />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p className={cn('text-[var(--color-ink)]', item.undone && 'line-through')}>{item.message}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-[var(--color-ink-3)]">
          <span>{item.actor?.name ?? 'Unattributed'}</span>
          <span aria-hidden>·</span>
          <time dateTime={item.at}>{when(item.at)}</time>
          {marker ? null : item.failed ? (
            <span className="flex items-center gap-1 text-[var(--color-leaving)]">
              <TriangleAlert size={11} />
              no longer applies
            </span>
          ) : null}
        </p>
      </div>
    </li>
  );
}

/**
 * A relative time for anything recent, an absolute one after that.
 *
 * "3 minutes ago" is what you want while you are working; "12 Aug" is what you
 * want when you open a plan from last month and are trying to remember when a
 * decision was taken.
 */
export function when(at: string, now: number = Date.now()): string {
  const then = Date.parse(at);
  if (!Number.isFinite(then)) return at;

  const seconds = Math.round((now - then) / 1000);
  if (seconds < 45) return 'just now';
  if (seconds < 5400) {
    const minutes = Math.round(seconds / 60);
    return minutes < 60 ? `${minutes} min ago` : '1 hour ago';
  }
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} hours ago`;
  if (seconds < 604_800) {
    const days = Math.round(seconds / 86_400);
    return days === 1 ? 'yesterday' : `${days} days ago`;
  }
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
